import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from './stores/auth';
import { AppShell } from './components/AppShell';
import { RequireAuth } from './components/RequireAuth';
import { FinancialFirewall } from './components/FinancialFirewall';
import { FeatureGate } from './components/FeatureGate';
import { PageSkeleton } from './components/Skeleton';
import { flush as flushOfflineQueue } from './lib/offlineQueue';

// Eagerly loaded (entry / very small / above-the-fold)
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

// Auth-flow helpers (small, public)
const ForgotPassword      = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword       = lazy(() => import('./pages/ResetPassword'));
const Signup              = lazy(() => import('./pages/Signup'));
const VerifyEmail         = lazy(() => import('./pages/VerifyEmail'));

// Lazily loaded routes (split into separate chunks)
const BAAOnboarding       = lazy(() => import('./pages/BAAOnboarding'));
const OnboardingWizard    = lazy(() => import('./pages/OnboardingWizard'));
const OnboardingComplete  = lazy(() => import('./pages/OnboardingWizard').then(m => ({ default: m.OnboardingComplete })));
const OnboardingPending   = lazy(() => import('./pages/OnboardingPending'));
const MySchedule          = lazy(() => import('./pages/Schedule')); // therapist gets same calendar, auto-scoped
const Patients            = lazy(() => import('./pages/Patients'));
const PatientDetail       = lazy(() => import('./pages/PatientDetail'));
const LeadPipeline        = lazy(() => import('./pages/LeadPipeline'));
const Appointments        = lazy(() => import('./pages/Appointments'));
const AppointmentDetail   = lazy(() => import('./pages/AppointmentDetail'));
const Schedule            = lazy(() => import('./pages/Schedule'));
const PlanMyDay           = lazy(() => import('./pages/PlanMyDay'));
const MapView             = lazy(() => import('./pages/MapView'));
const Documents           = lazy(() => import('./pages/Documents'));
const MyProfile           = lazy(() => import('./pages/MyProfile'));
const Team                = lazy(() => import('./pages/Team'));
const TherapistFolder     = lazy(() => import('./pages/TherapistFolder'));
const CredentialingDashboard = lazy(() => import('./pages/CredentialingDashboard'));
const KpiDashboard        = lazy(() => import('./pages/KpiDashboard'));
const ImportReport        = lazy(() => import('./pages/ImportReport'));
const ARTracker           = lazy(() => import('./pages/ARTracker'));
const Restricted          = lazy(() => import('./pages/Restricted'));
const Settings            = lazy(() => import('./pages/Settings'));
const Payroll              = lazy(() => import('./pages/Payroll'));
const Compliance          = lazy(() => import('./pages/Compliance'));
const ComplianceCenter    = lazy(() => import('./pages/ComplianceCenter'));
const SecurityCenter      = lazy(() => import('./pages/SecurityCenter'));
const Invoices            = lazy(() => import('./pages/Invoices'));
const InvoiceEditor       = lazy(() => import('./pages/InvoiceEditor'));
const PublicInvoice       = lazy(() => import('./pages/PublicInvoice'));
const PaymentResult       = lazy(() => import('./pages/PaymentResult'));
const AuditLogs           = lazy(() => import('./pages/AuditLogs'));
const VomPortal           = lazy(() => import('./pages/VomPortal'));
const VomAccess           = lazy(() => import('./pages/VomAccess'));
const SuperAdmin          = lazy(() => import('./pages/SuperAdmin'));
const SuperAdminComplianceRollup = lazy(() => import('./pages/SuperAdminComplianceRollup'));
const More                = lazy(() => import('./pages/More'));

import './App.css';
import { show_phi } from './lib/phiConfig';

// React Query: tuned staleTimes per data category (overridable per-call)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,           // 1 min default
      gcTime: 5 * 60_000,          // 5 min cache
    },
  },
});

function RoleRedirect() {
  const { profile, loading, initialized } = useAuth();
  if (!initialized || loading) return null;
  if (!profile) return <Navigate to="/login" replace />;
  const role = profile.role;
  if (role === 'super_admin') return <Navigate to="/super-admin" replace />;
  if (role === 'virtual_ops_manager') return <Navigate to="/vom" replace />;
  if (role === 'therapist') return <Navigate to="/my-schedule" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const init = useAuth(s => s.init);
  useEffect(() => { init(); }, [init]);

  // Flush offline mutation queue when we come back online
  useEffect(() => {
    const onOnline = () => {
      // No-op processor here: each feature module registers its own flusher.
      // For now we just notify; consumer code (e.g., Schedule) listens to `online` itself.
      flushOfflineQueue(async () => false).catch(() => {});
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/onboarding/baa" element={<RequireAuth><BAAOnboarding /></RequireAuth>} />
            <Route path="/onboarding" element={<RequireAuth><OnboardingWizard /></RequireAuth>} />
            <Route path="/onboarding/complete" element={<RequireAuth><OnboardingComplete /></RequireAuth>} />
            <Route path="/onboarding-pending" element={<RequireAuth><OnboardingPending /></RequireAuth>} />
            <Route path="/public/invoice/:token" element={<PublicInvoice />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
            <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route path="/" element={<RoleRedirect />} />
              <Route path="/dashboard" element={<RequireAuth roles={["org_admin", "therapist", "super_admin", "read_only_auditor"]}><Dashboard /></RequireAuth>} />
              <Route path="/my-schedule" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="schedule"><Schedule /></FeatureGate></RequireAuth>} />
              <Route path="/schedule" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="schedule"><Schedule /></FeatureGate></RequireAuth>} />
              <Route path="/schedule/plan-my-day" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="schedule"><PlanMyDay /></FeatureGate></RequireAuth>} />
              <Route path="/patients" element={<RequireAuth roles={["org_admin", ...(show_phi ? ["therapist"] : []), "virtual_ops_manager"]}><FeatureGate feature="patients"><Patients /></FeatureGate></RequireAuth>} />
              <Route path="/patients/:id" element={<RequireAuth roles={["org_admin", ...(show_phi ? ["therapist"] : []), "virtual_ops_manager"]}><FeatureGate feature="patients"><PatientDetail /></FeatureGate></RequireAuth>} />
              <Route path="/leads" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="patients"><LeadPipeline /></FeatureGate></RequireAuth>} />
              <Route path="/appointments" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="schedule"><Appointments /></FeatureGate></RequireAuth>} />
              <Route path="/appointments/:id" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="schedule"><AppointmentDetail /></FeatureGate></RequireAuth>} />
              <Route path="/map" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="schedule"><MapView /></FeatureGate></RequireAuth>} />
              <Route path="/documents" element={<FeatureGate feature="documents"><Documents /></FeatureGate>} />
              <Route path="/my-profile" element={<MyProfile />} />
              <Route path="/team" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="team_management"><Team /></FeatureGate></RequireAuth>} />
              <Route path="/team/:id" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager", "super_admin"]}><FeatureGate feature="team_management"><TherapistFolder /></FeatureGate></RequireAuth>} />
              <Route path="/credentialing" element={<RequireAuth roles={["org_admin", "therapist", "virtual_ops_manager"]}><FeatureGate feature="compliance"><CredentialingDashboard /></FeatureGate></RequireAuth>} />
              <Route path="/kpi" element={<FinancialFirewall><FeatureGate feature="kpi_analytics"><KpiDashboard /></FeatureGate></FinancialFirewall>} />
              <Route path="/kpi/import" element={<FinancialFirewall><FeatureGate feature="billing"><ImportReport /></FeatureGate></FinancialFirewall>} />
              <Route path="/kpi/ar" element={<FinancialFirewall><FeatureGate feature="billing"><ARTracker /></FeatureGate></FinancialFirewall>} />
              <Route path="/invoices" element={<FinancialFirewall><FeatureGate feature="billing"><Invoices /></FeatureGate></FinancialFirewall>} />
              <Route path="/invoices/new" element={<FinancialFirewall><FeatureGate feature="billing"><InvoiceEditor /></FeatureGate></FinancialFirewall>} />
              <Route path="/invoices/:id" element={<FinancialFirewall><FeatureGate feature="billing"><InvoiceEditor /></FeatureGate></FinancialFirewall>} />
              <Route path="/restricted" element={<Restricted />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/payroll" element={<FeatureGate feature="payroll"><Payroll /></FeatureGate>} />
              <Route path="/settings/compliance" element={<Compliance />} />
              <Route path="/settings/vom-access" element={<RequireAuth roles={["org_admin", "super_admin"]}><VomAccess /></RequireAuth>} />
              <Route path="/compliance" element={<RequireAuth roles={["org_admin", "super_admin"]}><FeatureGate feature="compliance"><ComplianceCenter /></FeatureGate></RequireAuth>} />
              <Route path="/security" element={<RequireAuth roles={["org_admin", "super_admin"]}><SecurityCenter /></RequireAuth>} />
              <Route path="/audit-logs" element={<RequireAuth roles={["org_admin", "super_admin", "read_only_auditor"]}><AuditLogs /></RequireAuth>} />
              <Route path="/vom" element={<RequireAuth roles={["virtual_ops_manager", "super_admin"]}><VomPortal /></RequireAuth>} />
              <Route path="/super-admin" element={<RequireAuth roles={["super_admin"]}><SuperAdmin /></RequireAuth>} />
              <Route path="/super-admin/compliance-rollup" element={<RequireAuth roles={["super_admin"]}><SuperAdminComplianceRollup /></RequireAuth>} />
              <Route path="/more" element={<More />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" toastOptions={{ style: { borderRadius: 12 } }} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
